/**
 * Iter-229d — Admin Event Detail Drawer.
 *
 * Right-rail drawer (modal on mobile) that opens when admin clicks an
 * event row in /admin/jobs-v2. Surfaces stat tiles + Shifts/Staffing/
 * Briefing/Activity tabs without leaving the list page.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Send, Pencil, Loader2, UserPlus } from 'lucide-react';
import { parseLocalDate, formatDate, formatTimeRange } from '../../utils/timeFormat';
import api from '../../utils/api';
import AdminStaffingRoster from './AdminStaffingRoster';
import EventScheduleEditor from '../EventScheduleEditor';
import InviteTalentModal from '../InviteTalentModal';   // Iter-360d (P0-356)
import { Button } from '../ui/button';

const Tab = ({ id, label, active, onClick }) => (
  <button onClick={() => onClick(id)}
          className={`flex-1 py-1.5 rounded-md text-xs font-semibold ${active ? 'bg-cyan-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
          data-testid={`drawer-tab-${id}`}>
    {label}
  </button>
);

export default function EventDetailDrawer({ event, onClose, onOpenShift, initialTab = 'shifts' }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(initialTab);
  // Iter-241 — hook MUST live above the early return below to satisfy
  // React's rules-of-hooks (otherwise the build errors out in CRA/eslint).
  const [sendingReminder, setSendingReminder] = useState(false);
  // Iter-360d (P0-356) — invite modal opened from the drawer.
  const [inviteOpen, setInviteOpen] = useState(false);
  React.useEffect(() => { setTab(initialTab); }, [initialTab, event?.key]);
  if (!event) return null;

  // Iter-234h — primary editable shift in the event. For multi-shift
  // events we point at the first shift; the admin can navigate to other
  // shifts via the Shifts tab. (Future: multi-shift bulk-edit UI.)
  const primaryShiftId = event.shifts?.[0]?.id;
  const openEditGig = () => {
    if (!primaryShiftId) return;
    onClose?.();
    navigate(`/admin/shifts/${primaryShiftId}?edit=1`);
  };

  const today = new Date().toISOString().slice(0, 10);
  const initials = (event.client_name || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  const fillPct = event.needed ? Math.round((event.filled / event.needed) * 100) : 0;
  const daysRemaining = (() => {
    if (!event.last_date || event.last_date < today) return 0;
    const d = parseLocalDate(event.last_date);
    if (!d) return 0;
    return Math.max(0, Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24)));
  })();

  // Iter-241 — Bulk pre-shift reminder. Email-only Phase A per user request.
  // Hits POST /admin/shifts/{shift_id}/send-reminder for every shift on this
  // event so each shift's staffed talent receive a personalised reminder.
  const sendPreShiftReminders = async () => {
    if (!event.shifts?.length) {
      toast.error('No shifts on this event');
      return;
    }
    setSendingReminder(true);
    let totalSent = 0;
    let totalRecipients = 0;
    let failures = 0;
    try {
      for (const shift of event.shifts) {
        if (!shift.id) continue;
        try {
          const res = await api.post(`/admin/shifts/${shift.id}/send-reminder`, {
            filter_type: 'all',
            channels: ['email'],
          });
          const data = res.data || {};
          totalRecipients += Number(data.recipients || 0);
          totalSent += Number((data.sent || {}).email || 0);
          failures += Number((data.failed || {}).email || 0);
        } catch (err) {
          // Surface individual-shift failures but keep iterating.
          failures += 1;
          // eslint-disable-next-line no-console
          console.error(`Reminder failed for shift ${shift.id}:`, err?.response?.data || err?.message);
        }
      }
      if (totalRecipients === 0) {
        toast.info('No eligible recipients (nobody staffed on these shifts yet).');
      } else if (failures > 0) {
        toast.warning(`Sent ${totalSent}/${totalRecipients} reminders. ${failures} failed — check audit log.`);
      } else {
        toast.success(`Sent ${totalSent} pre-shift reminder${totalSent === 1 ? '' : 's'} via email.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminder(false);
    }
  };

  // Briefing comes from the first shift in the series
  const briefing = event.shifts?.[0]?.event_briefing || {};
  const dressCode = event.shifts?.[0]?.dress_code || '';

  return (
    <Dialog open={!!event} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="p-0 sm:max-w-md sm:mr-0 sm:ml-auto sm:rounded-r-none sm:rounded-l-3xl sm:h-screen sm:max-h-screen h-[100dvh] max-h-[100dvh] overflow-y-auto overscroll-contain"
        style={{ WebkitOverflowScrolling: 'touch' }}
        data-testid="event-detail-drawer"
      >
        <DialogTitle className="sr-only">Event details</DialogTitle>
        <div className="p-5">
          {/* Iter-234e — removed custom close X; shadcn DialogContent
              already renders one in the top-right corner. Having both
              showed up as two stacked X buttons in production. */}
          <div className="flex items-center gap-2 mb-2 pr-7">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500 to-cyan-700 grid place-items-center text-white font-bold text-sm">{initials}</div>
            <div className="font-bold text-slate-900 truncate flex-1">{event.client_name}</div>
          </div>
          <h3 className="text-xl font-bold leading-tight" data-testid="drawer-event-title">{event.event_title}</h3>
          <p className="text-xs text-slate-500">
            {event.city}{event.state ? `, ${event.state}` : ''} · {formatDate(event.first_date)}
            {event.first_date !== event.last_date ? `–${formatDate(event.last_date)}` : ''} · {event.role_name} · {event.hourly_rate ? `$${event.hourly_rate}/hr` : ''}
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2 mb-4">
            {event.bucket === 'today' && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">● TODAY</span>}
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200">{event.filled}/{event.needed || '?'} staffed</span>
            {event.applicants > 0 && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{event.applicants} applicants</span>}
          </div>

          {/* Stat tiles */}
          <div className="grid grid-cols-3 gap-2 text-center mb-4">
            <div className="bg-slate-50 rounded-lg p-2"><div className="text-[10px] uppercase font-semibold text-slate-500">Fill rate</div><div className="font-bold">{fillPct}%</div></div>
            <div className="bg-slate-50 rounded-lg p-2"><div className="text-[10px] uppercase font-semibold text-slate-500">Applicants</div><div className="font-bold">{event.applicants || '—'}</div></div>
            <div className="bg-slate-50 rounded-lg p-2"><div className="text-[10px] uppercase font-semibold text-slate-500">Days left</div><div className="font-bold">{daysRemaining}</div></div>
          </div>

          {/* Iter-360d (P0-356) — Unfilled positions CTA. Renders on
              every drawer surface where `filled < needed`. Independent
              of the full AdminShiftDetail page — Ops must never have to
              navigate away to find the Invite Talent action. */}
          {primaryShiftId && Number(event.filled ?? 0) < Number(event.needed ?? 0) && (
            <div
              className="mb-3 flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-cyan-300 bg-cyan-50 px-3 py-2.5"
              data-testid="drawer-unfilled-positions-banner"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-cyan-900" data-testid="drawer-unfilled-headline">
                  {Math.max(0, Number(event.needed ?? 0) - Number(event.filled ?? 0))}
                  {' '}open position{Math.max(0, Number(event.needed ?? 0) - Number(event.filled ?? 0)) === 1 ? '' : 's'}
                  {' '}· {Number(event.filled ?? 0)} of {Number(event.needed ?? 0)} staffed
                </p>
                <p className="text-[11px] text-cyan-800/80 leading-4">
                  Send invites to matching talent to fill the remaining position{Math.max(0, Number(event.needed ?? 0) - Number(event.filled ?? 0)) === 1 ? '' : 's'}.
                </p>
              </div>
              <Button
                onClick={() => setInviteOpen(true)}
                className="bg-cyan-600 hover:bg-cyan-700 text-white sm:shrink-0"
                data-testid="drawer-invite-talent-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" /> Invite Talent
              </Button>
            </div>
          )}

          {/* Iter-234h — Edit Gig CTA (primary action on the drawer).
              Navigates to /admin/shifts/{id}?edit=1 which auto-enters
              the full edit form on AdminShiftDetail, with the dress
              code preset, grooming, and send-home warning all editable. */}
          <Button
            className="w-full mb-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold flex items-center justify-center gap-1.5"
            onClick={openEditGig}
            disabled={!primaryShiftId}
            data-testid="drawer-edit-gig"
          >
            <Pencil className="w-3.5 h-3.5" /> Edit Gig
          </Button>

          {/* Send pre-shift reminder action — Iter-241: now wired to
              POST /admin/shifts/{id}/send-reminder, email-only Phase A. */}
          <button
            className="w-full mb-4 bg-cyan-50 hover:bg-cyan-100 disabled:opacity-60 disabled:cursor-not-allowed border border-cyan-200 text-cyan-700 rounded-lg py-2 text-xs font-semibold flex items-center justify-center gap-1.5"
            onClick={sendPreShiftReminders}
            disabled={sendingReminder || !event.shifts?.length}
            data-testid="send-reminder"
          >
            {sendingReminder ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending email reminders…</>
            ) : (
              <><Send className="w-3.5 h-3.5" /> Send pre-shift reminder to all staffed talent (email)</>
            )}
          </button>

          {/* Tabs */}
          <div className="border border-slate-200 rounded-lg p-0.5 flex flex-wrap mb-3">
            <Tab id="shifts"   label={`Shifts (${event.total_shifts})`} active={tab === 'shifts'}   onClick={setTab} />
            <Tab id="schedule" label="Schedule"                          active={tab === 'schedule'} onClick={setTab} />
            <Tab id="staffing" label="Staffing"                          active={tab === 'staffing'} onClick={setTab} />
            <Tab id="briefing" label="Briefing"                          active={tab === 'briefing'} onClick={setTab} />
            <Tab id="activity" label="Activity"                          active={tab === 'activity'} onClick={setTab} />
          </div>

          {tab === 'shifts' && (
            <ol className="space-y-1.5">
              {event.shifts.map((s, i) => {
                const isT = s.shift_date === today;
                const isPast = (s.shift_date || '') < today;
                const isMissed = isPast && (s.workers_filled === 0 || s.workers_filled == null);
                const cls = isT
                  ? 'bg-cyan-50/60 border-2 border-cyan-500'
                  : isMissed
                  ? 'bg-rose-50/60 border border-rose-200'
                  : 'bg-slate-50 border border-slate-200';
                const badgeCls = isT
                  ? 'bg-cyan-600 text-white'
                  : isMissed
                  ? 'bg-rose-100 text-rose-700'
                  : 'bg-slate-200 text-slate-700';
                return (
                  <li key={s.id}
                      onClick={() => onOpenShift?.(s)}
                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:shadow-sm ${cls}`}
                      data-testid={`drawer-shift-${s.id}`}>
                    <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${badgeCls}`}>D{i + 1}</span>
                    <span className={`text-xs ${isT ? 'font-bold' : ''}`}>
                      {formatDate(s.shift_date)}{s.start_time && s.end_time ? ` · ${formatTimeRange(s.start_time, s.end_time)}` : ''}
                      {isT ? ' · TODAY' : ''}
                    </span>
                    <span className="ml-auto text-[10px] font-semibold text-slate-600">
                      {s.workers_filled || 0}/{s.workers_needed || 0}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}

          {tab === 'staffing' && (
            <AdminStaffingRoster
              shiftIds={(event.shifts || []).map((s) => s.id)}
              eventTitle={event.event_title}
            />
          )}

          {tab === 'schedule' && (
            <EventScheduleEditor
              shiftId={primaryShiftId}
              onClose={() => setTab('shifts')}
            />
          )}

          {tab === 'briefing' && (
            <div className="space-y-2 text-xs" data-testid="drawer-briefing">
              {dressCode && <div><span className="font-semibold text-slate-700">Dress code: </span><span className="text-slate-600">{dressCode}</span></div>}
              {briefing.grooming && <div><span className="font-semibold text-slate-700">Grooming: </span><span className="text-slate-600">{briefing.grooming}</span></div>}
              {briefing.parking_info && <div><span className="font-semibold text-slate-700">Parking: </span><span className="text-slate-600">{briefing.parking_info}</span></div>}
              {briefing.contact_name && <div><span className="font-semibold text-slate-700">Contact: </span><span className="text-slate-600">{briefing.contact_name} {briefing.contact_role ? `· ${briefing.contact_role}` : ''}{briefing.contact_phone ? ` · ${briefing.contact_phone}` : ''}</span></div>}
              {briefing.sent_home_policy && <div><span className="font-semibold text-slate-700">Sent-home policy: </span><span className="text-slate-600">{briefing.sent_home_policy}</span></div>}
              {briefing.reporting_instructions && <div><span className="font-semibold text-slate-700">Reporting: </span><span className="text-slate-600">{briefing.reporting_instructions}</span></div>}
              {!dressCode && !briefing.grooming && !briefing.parking_info && !briefing.contact_name && !briefing.sent_home_policy && !briefing.reporting_instructions && (
                <p className="text-slate-400 italic">No briefing yet — encourage the client/agency to fill the Event Briefing section when creating the job.</p>
              )}
            </div>
          )}

          {tab === 'activity' && (
            <p className="text-xs text-slate-500 italic" data-testid="drawer-activity">Activity feed coming soon.</p>
          )}
        </div>
      </DialogContent>

      {/* Iter-360d (P0-356) — Invite modal mounted at the drawer level
          so it renders above the drawer overlay. `shiftMeta` gives the
          modal the venue city/state for its "only matching location"
          hint. */}
      {primaryShiftId && (
        <InviteTalentModal
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          shiftId={primaryShiftId}
          shiftMeta={{
            venue_name: event.shifts?.[0]?.venue_name,
            city: event.shifts?.[0]?.city,
            state: event.shifts?.[0]?.state,
            market_id: event.shifts?.[0]?.market_id,
          }}
        />
      )}
    </Dialog>
  );
}
